import { Result } from "../../api/types";
import { dateTimeNowForExcel } from "../../utils/format";
import type { ReviewRowOnSheet } from "../../integrations/qto/reviewRow";
import { REVIEW_DATA_START_ROW, SHEET_NAME } from "./reviewSheetLayout";
import { browserStore } from "../../utils/storageKeys";

const LOG_SHEET_NAME = "QTO Review Log";

const LOG_TABLE_NAME = "QTOReviewCompleteLog";

const LOG_TABLE_HEADERS = [
  "Action",
  "Type",
  "Line Item ID",
  "Order",
  "Takeoff Name",
  "Takeoff Qty",
  "Takeoff UoM",
  "Linked Item Name",
  "Linked Item Qty",
  "Linked Item UoM",
  "Product ID",
  "user",
  "submitted at",
] as const;

export interface AfterReviewSubmitWorkbookState {
  /** No Add/Update/Ignore rows remain on QTO Review after removing submitted rows. */
  reviewSheetEmpty: boolean;
  /** Workbook has more than one sheet, so QTO Review may be deleted. */
  canDeleteReviewSheet: boolean;
}

/**
 * After a successful import: append submitted rows to the hidden log table and remove them from QTO Review (bottom-up).
 */

export async function appendQtoReviewLogAndDeleteRows(
  submitted: ReviewRowOnSheet[]
): Promise<AfterReviewSubmitWorkbookState | null> {
  if (submitted.length === 0) return null;

  let reviewSheetEmpty = false;
  let canDeleteReviewSheet = false;

  await Excel.run(async (context) => {
    let logSheet = context.workbook.worksheets.getItemOrNullObject(LOG_SHEET_NAME);
    await context.sync();

    if (logSheet.isNullObject) {
      logSheet = context.workbook.worksheets.add(LOG_SHEET_NAME);
    }
    logSheet.visibility = Excel.SheetVisibility.hidden;

    let table = logSheet.tables.getItemOrNullObject(LOG_TABLE_NAME);
    await context.sync();

    const expectedHeaders = Array.from(LOG_TABLE_HEADERS);

    if (table.isNullObject) {
      const headerRange = logSheet.getRange(`A1:${String.fromCharCode(64 + expectedHeaders.length)}1`);
      headerRange.values = [expectedHeaders];
      table = logSheet.tables.add(headerRange, true);
      table.name = LOG_TABLE_NAME;
    } else {
      let headerRowRange = table.getHeaderRowRange();
      headerRowRange.load("values");
      table.columns.load("count");
      await context.sync();
      let headers = headerRowRange.values[0] as string[];

      if (table.columns.count === 11 && String(headers[2] ?? "").trim() === "Takeoff Name") {
        table.columns.add(2);
        await context.sync();
      }

      headerRowRange = table.getHeaderRowRange();
      headerRowRange.load("values");
      table.columns.load("count");
      await context.sync();
      headers = headerRowRange.values[0] as string[];

      if (
        table.columns.count === 12 &&
        (String(headers[1] ?? "").trim() === "Link" || String(headers[1] ?? "").trim() === "Type") &&
        String(headers[2] ?? "").trim() === "Order"
      ) {
        table.columns.add(2);
        await context.sync();
      }

      headerRowRange = table.getHeaderRowRange();
      headerRowRange.load("values");
      table.columns.load("count");
      await context.sync();
      headers = headerRowRange.values[0] as string[];

      if (
        table.columns.count === expectedHeaders.length &&
        (headers.length !== expectedHeaders.length ||
          expectedHeaders.some((h, i) => String(headers[i] ?? "").trim() !== h))
      ) {
        table.getHeaderRowRange().values = [expectedHeaders];
      }
    }

    await context.sync();

    const userEmail = browserStore.get("userEmail") ?? "";
    const submittedAt = dateTimeNowForExcel();

    const valueRows: (string | number)[][] = submitted.map((r) => [
      r.action,
      r.link,
      r.lineItemId,
      r.order,
      r.takeoffName,
      r.takeoffQty,
      r.takeoffUom,
      r.lineItemName,
      r.lineItemQty,
      r.lineItemUom,
      r.productId,
      userEmail,
      submittedAt,
    ]);

    table.rows.add(-1, valueRows);

    const reviewSheet = context.workbook.worksheets.getItem(SHEET_NAME);
    const excelRowsDesc = Array.from(new Set(submitted.map((r) => r.excelRow))).sort((a, b) => b - a);
    for (const excelRow of excelRowsDesc) {
      reviewSheet.getRange(`${excelRow}:${excelRow}`).delete(Excel.DeleteShiftDirection.up);
    }

    await context.sync();

    // If no review data rows remain, remove the sheet (unless it is the only worksheet).
    const usedAfter = reviewSheet.getUsedRange();
    usedAfter.load("rowCount");
    await context.sync();

    const startRow = REVIEW_DATA_START_ROW;
    let hasReviewRow = false;
    if (usedAfter.rowCount >= startRow) {
      const lastRow = usedAfter.rowCount;
      const dataRange = reviewSheet.getRange(`B${startRow}:M${lastRow}`);
      dataRange.load("values");
      await context.sync();
      const rows = dataRange.values as unknown[][];

      // Consider any non-header row that still has data in the review table columns
      // as a remaining user review record. Only when all such rows are cleared
      // should the sheet be considered empty and eligible for deletion.
      for (const row of rows) {
        const hasAnyData = row.some((cell) => {
          const value = typeof cell === "string" ? cell.trim() : cell;
          return value !== "" && value != null;
        });
        if (hasAnyData) {
          hasReviewRow = true;
          break;
        }
      }
    }

    if (!hasReviewRow) {
      reviewSheetEmpty = true;
      context.workbook.worksheets.load("items");
      await context.sync();
      canDeleteReviewSheet = context.workbook.worksheets.items.length > 1;
    }

    await context.sync();
  });

  return { reviewSheetEmpty, canDeleteReviewSheet };
}

/** Delete the QTO Review worksheet if it exists and the workbook has other sheets. */

export async function deleteQtoReviewWorksheet(): Promise<Result<void>> {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(SHEET_NAME);
      await context.sync();
      if (sheet.isNullObject) return;

      context.workbook.worksheets.load("items");
      await context.sync();
      if (context.workbook.worksheets.items.length <= 1) return;

      sheet.delete();
      await context.sync();
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
